import { useQuery } from '@tanstack/react-query';
import { rbacApi } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import type { TeamRole, Permission } from '../lib/roles';
import { CAN_SUBMIT_ROLES, CAN_REVIEW_ROLES } from '../lib/roles';

interface UseTeamRoleResult {
  role: TeamRole | null;
  permissions: Permission[];
  isLoading: boolean;
  /** Check if user has a specific permission */
  hasPermission: (permission: Permission) => boolean;
  /** Check if user can submit reports for review */
  canSubmit: boolean;
  /** Check if user can approve/reject reports */
  canReview: boolean;
  /** Check if user is team owner */
  isOwner: boolean;
}

/**
 * Hook to fetch and cache the current user's role and permissions for a team.
 * Falls back to basic owner/non-owner checks for non-Enterprise plans.
 */
export function useTeamRole(teamId: string | undefined): UseTeamRoleResult {
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['team-role', teamId],
    queryFn: async () => {
      const response = await rbacApi.getMyRole(teamId!);
      return response.data as { role: TeamRole; permissions: Permission[] };
    },
    enabled: !!teamId && !!user,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
    retry: false,
  });

  const role = data?.role ?? null;
  const permissions = data?.permissions ?? [];

  return {
    role,
    permissions,
    isLoading,
    hasPermission: (permission: Permission) => permissions.includes(permission),
    canSubmit: role ? CAN_SUBMIT_ROLES.includes(role) : false,
    canReview: role ? CAN_REVIEW_ROLES.includes(role) : false,
    isOwner: role === 'OWNER',
  };
}
